import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerProps,
  FormControl,
  FormLabel,
  Stack,
  useToast,
} from '@chakra-ui/react';
import { usePostUserMutation } from 'api';
import { PostUserRequest } from 'api/bindings/PostUserRequest';
import { AppInput, FormButtonGroup } from 'features/components';
import { useCallback, useEffect, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

export interface CreateUserDrawerProps
  extends Omit<DrawerProps, 'onSubmit' | 'children'> {
  onClose: () => void;
}

interface Form extends PostUserRequest {}

export const CreateUserDrawer = (props: CreateUserDrawerProps) => {
  const { onClose, ...rest } = props;

  const toast = useToast();
  const [postUser] = usePostUserMutation();

  // State
  const form = useForm<Form>({
    defaultValues: {
      // firstName: '',
      // lastName: '',
      // preferredName: null,
      // email: '',
      // password: '',
      // confirmPassword: '',
      // purchaseOrderApprovalAuthority: undefined,
      // workOrderApprovalAuthority: undefined,
      // invoiceApprovalAuthority: undefined,
      // afeApprovalAuthority: undefined,
      // roles: null,
      // claims: null,
    },
    mode: 'all',
  });

  const { register, control, watch, handleSubmit, formState, reset } = form;

  const [firstName, preferredName, lastName] = watch([
    'firstName',
    'preferredName',
    'lastName',
  ]);
  const formName = useMemo(
    () => `Create User: ${preferredName || firstName || ''} ${lastName || ''}`,
    [firstName, lastName, preferredName]
  );

  const [password, confirmPassword] = watch(['password', 'confirmPassword']);
  const isPasswordsMatch = useMemo(
    () => password === confirmPassword,
    [confirmPassword, password]
  );
  const isValidPassword = useCallback(
    () =>
      ((!password || !confirmPassword || password !== confirmPassword) &&
        `Password must be valid and match`) ||
      undefined,
    [confirmPassword, password]
  );

  const onSubmit = useCallback(
    async (form: Form) => {
      try {
        const result = await postUser(form);
        if ('error' in result) {
          toast({
            title: `Failed to post user ${
              form.preferredName || form.firstName
            }`,
            status: 'error',
            description: (result.error as any)?.data?.message,
          });
          return;
        }
        toast({
          title: `User ${
            form.preferredName || form.firstName
          } posted successfully!`,
          status: 'success',
        });
        reset();
        onClose();
      } catch {}
    },
    [postUser, toast]
  );

  // const onSubmit = async (data: Form) => {
  // if (!passwordsMatch) {
  //   toast({
  //     title: 'Password Mismatch',
  //     description: 'Password and confirm password must match',
  //     status: 'error',
  //     duration: 3000,
  //     isClosable: true,
  //   });
  //   return;
  // }

  //   try {
  //     await createUser({
  //       ...data,
  //       // clientId: BigInt(clientId),
  //       purchaseOrderApprovalAuthority:
  //         data.purchaseOrderApprovalAuthority || '0',
  //       workOrderApprovalAuthority: data.workOrderApprovalAuthority || '0',
  //       invoiceApprovalAuthority: data.invoiceApprovalAuthority || '0',
  //       afeApprovalAuthority: data.afeApprovalAuthority || '0',
  //     }).unwrap();
  //   } catch (err) {
  //     toast({
  //       title: 'Error Creating User',
  //       description: 'Failed to create user. Please try again.',
  //       status: 'error',
  //       duration: 5000,
  //       isClosable: true,
  //     });
  //   }
  // };

  // useEffect(() => {
  //   if (isSuccess) {
  //     toast({
  //       title: 'User Created',
  //       description: 'User has been successfully created',
  //       status: 'success',
  //       duration: 3000,
  //       isClosable: true,
  //     });
  //     onSuccess?.();
  //     onClose();
  //   }
  // }, [isSuccess, onSuccess, onClose, toast]);

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Drawer onClose={onClose} {...rest} size={'xl'}>
          <DrawerOverlay />
          <DrawerCloseButton />
          <DrawerContent>
            <DrawerHeader>{formName}</DrawerHeader>
            <DrawerBody>
              <Stack spacing={4}>
                <FormControl isRequired>
                  <FormLabel htmlFor="firstName">First Name:</FormLabel>
                  <Controller
                    control={control}
                    name="firstName"
                    rules={{
                      required: {
                        value: true,
                        message: 'First name is required',
                      },
                      minLength: {
                        value: 3,
                        message: `First name must be at least 3 characters.`,
                      },
                      maxLength: {
                        value: 256,
                        message: `First name cannot be more than 256 characters.`,
                      },
                    }}
                    render={(f) => (
                      <AppInput id="firstName" {...(f.field as any)} />
                    )}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="preferredName">Preferred Name:</FormLabel>
                  {/* <AppInput id="preferred" {...register('preferredName')} /> */}
                  <Controller
                    control={control}
                    name="preferredName"
                    rules={{
                      minLength: {
                        value: 3,
                        message: `Preferred name must be at least 3 characters.`,
                      },
                      maxLength: {
                        value: 256,
                        message: `Preferred name must be at most 256 characters.`,
                      },
                    }}
                    render={(f) => (
                      <AppInput id="preferredName" {...(f.field as any)} />
                    )}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel htmlFor="lastName">Last Name:</FormLabel>
                  <Controller
                    control={control}
                    name="lastName"
                    rules={{
                      required: {
                        value: true,
                        message: `Last name is required`,
                      },
                      minLength: {
                        value: 3,
                        message: `Last name must be at least 3 characters.`,
                      },
                      maxLength: {
                        value: 256,
                        message: `Last name must be no more than 256 characters.`,
                      },
                    }}
                    render={(f) => (
                      <AppInput id="lastName" {...(f.field as any)} />
                    )}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel htmlFor="email">Email:</FormLabel>
                  <Controller
                    control={control}
                    name="email"
                    rules={{
                      required: {
                        value: true,
                        message: 'Email is required',
                      },
                      pattern: {
                        value:
                          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                        message: `Email must be a valid email address`,
                      },
                    }}
                    render={(f) => (
                      <AppInput type="email" id="email" {...(f.field as any)} />
                    )}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel htmlFor="password">Password:</FormLabel>
                  <Controller
                    control={control}
                    name="password"
                    rules={{
                      required: {
                        value: true,
                        message: 'Password is required.',
                      },
                      pattern: {
                        value:
                          /^(?!\s+)(?!.*\s+$)(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[$^*.[\]{}()?"!@#%&/\\,><':;|_~`=+\- ])[A-Za-z0-9$^*.[\]{}()?"!@#%&/\\,><':;|_~`=+\- ]{8,256}$/,
                        message: `Password must be valid`,
                      },
                    }}
                    render={(f) => (
                      <AppInput
                        id="password"
                        type="password"
                        {...(f.field as any)}
                      />
                    )}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel htmlFor="confirmPassword">
                    Confirm Password:
                  </FormLabel>
                  <Controller
                    control={control}
                    name="confirmPassword"
                    rules={{
                      required: {
                        value: true,
                        message: 'Confirmation of password is required',
                      },
                      pattern: {
                        value:
                          /^(?!\s+)(?!.*\s+$)(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[$^*.[\]{}()?"!@#%&/\\,><':;|_~`=+\- ])[A-Za-z0-9$^*.[\]{}()?"!@#%&/\\,><':;|_~`=+\- ]{8,256}$/,
                        message: `Password must be valid`,
                      },
                      validate: {
                        isValidPassword,
                      },
                    }}
                    render={(f) => (
                      <AppInput
                        id="confirmPassword"
                        type="password"
                        {...(f.field as any)}
                      />
                    )}
                  />
                </FormControl>
              </Stack>
              <Stack spacing={4} mt={4}>
                <FormControl>
                  <FormLabel htmlFor="afe">AFE Authority:</FormLabel>
                  <Controller
                    control={control}
                    name="afeApprovalAuthority"
                    rules={{
                      min: {
                        value: 0,
                        message: 'AFE approval authority must be $0 or higher.',
                      },
                    }}
                    render={(f) => (
                      <AppInput
                        id="afe"
                        type="currency"
                        {...(f.field as any)}
                      />
                    )}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="po">Purchase Order Authority:</FormLabel>
                  <Controller
                    control={control}
                    name="purchaseOrderApprovalAuthority"
                    rules={{
                      min: {
                        value: 0,
                        message:
                          'Purchase Order authority must be $0 or higher.',
                      },
                    }}
                    render={(f) => (
                      <AppInput id="po" type="currency" {...(f.field as any)} />
                    )}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="wo">Work Order Authority:</FormLabel>
                  <Controller
                    control={control}
                    name="workOrderApprovalAuthority"
                    rules={{
                      min: {
                        value: 0,
                        message: 'Work Order authority must be $0 or higher',
                      },
                    }}
                    render={(f) => (
                      <AppInput id="wo" type="currency" {...(f.field as any)} />
                    )}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="invoice">Invoice Authority:</FormLabel>
                  <Controller
                    control={control}
                    name="invoiceApprovalAuthority"
                    rules={{
                      min: {
                        value: 0,
                        message: 'Invoice authority must be $0 or higher.',
                      },
                    }}
                    render={(f) => (
                      <AppInput
                        id="invoice"
                        type="currency"
                        {...(f.field as any)}
                      />
                    )}
                  />
                </FormControl>
              </Stack>
            </DrawerBody>
            <DrawerFooter>
              <FormButtonGroup
                onCancel={onClose}
                onConfirm={handleSubmit(onSubmit)}
              />
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      </form>
    </FormProvider>
  );
};
